import { motion } from "framer-motion";
import { Phone, Mail, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

// Bloc affiché tant qu'une information officielle n'a pas été transmise.
// Aucune donnée fictive n'est publiée.
const Pending = ({ label }) => (
  <div
    className="mt-2 flex items-start gap-3 border border-dashed border-amber-400 bg-amber-50 p-4 text-sm text-amber-900"
    data-testid={`pending-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
  >
    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
    <p>
      <span className="font-bold">Information en attente :</span> {label} — en
      cours de transmission par ATLAS THD. Cette mention sera complétée avec
      les données officielles avant publication.
    </p>
  </div>
);

const Section = ({ title, children, testId }) => (
  <motion.section {...fadeUp} transition={{ duration: 0.5 }} className="border border-line bg-white p-6 sm:p-10" data-testid={testId}>
    <h2 className="font-display text-xl font-extrabold tracking-tight text-midnight sm:text-2xl">
      {title}
    </h2>
    <div className="mt-5 space-y-3 text-sm leading-relaxed text-steel sm:text-base">
      {children}
    </div>
  </motion.section>
);

const MentionsLegales = () => (
  <>
    <Seo
      title="Mentions légales — ATLAS THD"
      description="Mentions légales du site ATLAS THD : identité de l'entreprise, hébergement, propriété intellectuelle et coordonnées officielles."
      breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "Mentions légales" }]}
    />

    <section className="border-b border-line bg-cloud" data-testid="legal-hero">
      <div className="bg-blueprint">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
          <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Mentions légales" }]} />
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-6 font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl"
          >
            Mentions légales
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 max-w-2xl text-lg text-steel"
          >
            Informations officielles relatives à l'éditeur du site, à
            l'hébergement et à la propriété intellectuelle.
          </motion.p>
        </div>
      </div>
    </section>

    <div className="bg-cloud py-16 lg:py-20" data-testid="legal-content">
      <div className="mx-auto max-w-4xl space-y-8 px-4 sm:px-6 lg:px-8">
        <Section title="Éditeur du site" testId="legal-editeur">
          <p>
            Le site est édité par <span className="font-semibold text-midnight">ATLAS THD</span>,
            entreprise spécialisée dans le raccordement fibre optique
            (FTTO/FTTH), l'inspection technique et le contrôle qualité des
            infrastructures télécoms.
          </p>
          <p>
            Siège social : 1 B Rue du Docteur Laennec, 59139 Wattignies.
          </p>
          <p>
            SIREN : 109 031 385.
          </p>
          <p>
            Immatriculée au RCS de Lille Métropole sous le numéro 109 031 385.
          </p>
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:gap-8">
            <a href="tel:+33605765066" data-testid="legal-phone" className="flex items-center gap-2 font-medium text-midnight transition-colors hover:text-terrain">
              <Phone className="h-4 w-4 text-terrain" />
              06 05 76 50 66
            </a>
            <a href="mailto:Contact.pro@atlasthd.com" data-testid="legal-email" className="flex items-center gap-2 font-medium text-midnight transition-colors hover:text-terrain">
              <Mail className="h-4 w-4 text-terrain" />
              Contact.pro@atlasthd.com
            </a>
          </div>
        </Section>

        <Section title="Directeur de la publication" testId="legal-directeur">
          <p>
            Le directeur de la publication est{" "}
            <span className="font-semibold text-midnight">M. Juba Bouchal</span>,
            représentant légal d'ATLAS THD.
          </p>
        </Section>

        <Section title="Hébergement du site" testId="legal-hebergement">
          <p>
            Le site est hébergé par <span className="font-semibold text-midnight">IONOS SARL</span>,
            7 place de la Gare, BP 70109, 57200 Sarreguemines Cedex, France.
          </p>
          <p>Téléphone : 0970 808 911.</p>
        </Section>

        <Section title="Propriété intellectuelle" testId="legal-propriete">
          <p>
            L'ensemble des contenus du site (textes, éléments graphiques, logo,
            photographies) est la propriété d'ATLAS THD ou fait l'objet d'une
            autorisation d'utilisation. Toute reproduction, représentation ou
            réutilisation, totale ou partielle, sans autorisation écrite
            préalable est interdite.
          </p>
          <p>
            Les photographies de chantiers et d'interventions publiées sur ce
            site sont fournies par ATLAS THD. Les visuels d'illustration
            proviennent de la banque d'images Unsplash.
          </p>
        </Section>

        <Section title="Données personnelles" testId="legal-donnees">
          <p>
            Les informations transmises via le formulaire de contact sont
            traitées conformément à notre{" "}
            <a href="/politique-confidentialite" data-testid="legal-privacy-link" className="font-medium text-midnight underline decoration-terrain/50 underline-offset-4 hover:text-terrain">
              politique de confidentialité
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  </>
);

export default MentionsLegales;
